import { useMemo } from 'react';
import { CalendarRange, Users, Image as ImageIcon, BarChart3, Megaphone } from '../../../../icons';
import { TYPE_COLOR_MAP, type CalendarEventDto, type CalendarEventType } from '../../../../api/calendar';
import type { ReservationDto, ReservationResourceDto } from '../../../../api/reservation';
// 종일·여러 날 일정의 열 배치 — 캘린더 월뷰와 «같은» 계산을 쓴다(그쪽에 시험이 있다).
import { layoutWeek } from '../../../CalendarPage/lib/monthLayout';
import type { NoticeListItem } from '../../../../api/board';
import { getDateLocale } from '../../../../lib/base-i18n';
import { useMenuGate } from '../../../../lib/menu-gate';
import { leaveAvailable } from '../../../../lib/leave-balance';
import { boxPath } from '../../../ApprovalPage/box/approval-box-types';
import { WidgetCard } from '../WidgetCard';
import { useWidgetChrome } from '../widget-chrome';
import { useWidgetActions } from '../widget-context';
// 대시보드 시안 v2 V3a (2026-09-16) — 보이는 지표 · 순서 · 기간 · 비교를 읽는 판. 깃발이 꺼져 있으면 아래 옛 판 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../../lib/layout-shell';
import { KpiStripWidgetV2 } from './v2/KpiStripWidgetV2';
import {
  useMonthEvents, useRoomStatus, usePhotoWall, useNotices,
  useLeaveSummary, useMonthDailyWork, useCompletedApprovals, useProjectCount,
} from '../widget-queries';

/**
 * 풀사이즈(통짜) 위젯 — 시안 화면 F (2026-09-09 · 판 5).
 *
 * 가로가 넓어야 뜻이 통하는 것들이다. 전부 머리 없는 카드(`WidgetCard`) 위에 그린다.
 *
 * ⚠ **시안 화면 F 의 ⑥ 「사내 방송」은 여기 없다** — 판 3 에서 이미 만들었다
 *   (`BroadcastWidget`: 썸네일 · 재생 · 이어보기 · 진행 막대까지 같은 것). 같은 물건을 두 벌
 *   만들면 「어느 쪽을 고쳐야 하나」가 생긴다.
 * ⚠ 시안의 색 그라디언트는 인라인 hex 였다(시안은 «그림»이라 그래도 된다). 제품에서는
 *   **회사가 고른 테마를 따라야** 하므로 전부 `--primary` 계열 토큰에서 파생시킨다.
 */

/* ── ① 주간 타임라인 ────────────────────────────────────────────────────── */

/** 이 주(월~일)의 시작. 일요일이면 지난 월요일로 당긴다. */
function weekStart(base: Date): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const dow = (d.getDay() + 6) % 7;   // 월=0 … 일=6
  d.setDate(d.getDate() - dow);
  return d;
}

/** 타임라인이 그리는 시간대 — 09시부터 19시까지. 그 밖의 일정은 가장자리에 붙는다. */
const DAY_FROM_MIN = 9 * 60;
const DAY_TO_MIN = 19 * 60;
const DAY_SPAN_MIN = DAY_TO_MIN - DAY_FROM_MIN;

function minutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 주간 타임라인 위젯 (시안 화면 F ①).
 *
 * ★★ 한 주가 **달을 넘길 수 있다** — 그때 달 하나만 받으면 월요일 쪽 이틀이 통째로 빈다.
 *    그래서 걸치는 달이 둘이면 두 번 받아 합친다(같은 캐시 키를 캘린더 위젯과 나눠 쓴다).
 * ★ 종일 일정은 시간 칸에 못 그린다 — 날짜 머리 아래 «종일» 줄로 뺀다.
 */
export function WeekTimelineWidget() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('calendar', '캘린더');
  const today = new Date();
  const start = weekStart(today);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);

  const a = useMonthEvents(start.getFullYear(), start.getMonth(), gate.enabled);
  const spansTwo = end.getMonth() !== start.getMonth();
  const b = useMonthEvents(end.getFullYear(), end.getMonth(), gate.enabled && spansTwo);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)),
    [start.getFullYear(), start.getMonth(), start.getDate()],
  );

  /** 이 주에 걸치는 일정 — 달 둘을 받았을 때 같은 것이 두 번 오므로 id 로 한 번만 남긴다. */
  const events = useMemo(() => {
    const seen = new Set<string>();
    const out: CalendarEventDto[] = [];
    for (const e of [...(a.data ?? []), ...(spansTwo ? b.data ?? [] : [])]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
    return out;
  }, [a.data, b.data, spansTwo]);

  /**
   * ★★★ 종일·여러 날 일정은 **캘린더 월뷰가 쓰는 배치를 그대로 빌려 쓴다**
   *     (`CalendarPage/lib/monthLayout.ts` · 시험 있음). 여기서 다시 세면
   *     **「끝이 자정이면 하루 더」 같은 어긋남**을 또 만들게 된다 — 그 함수는 이미 그것을 처리한다.
   * ★ 레인은 둘까지만 — 종일 줄이 길어지면 정작 시간 칸이 짜부라진다. 넘치는 것은 `+N`.
   */
  const allDay = useMemo(() => layoutWeek(start, events.filter((e) => e.isAllDay), 2), [start.getTime(), events]);

  /** 시간 칸에는 **시각이 있는 것만** 그린다 — 종일은 위의 줄이 맡는다. */
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    for (const e of events) {
      if (e.isAllDay) continue;
      const d = new Date(e.startAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const arr = map.get(key);
      if (arr) arr.push(e); else map.set(key, [e]);
    }
    return map;
  }, [events]);

  const nowMin = today.getHours() * 60 + today.getMinutes();
  const nowPct = ((nowMin - DAY_FROM_MIN) / DAY_SPAN_MIN) * 100;

  const loading = a.loading || (spansTwo && b.loading);
  const error = a.error ?? (spansTwo ? b.error : null);

  return (
    <WidgetCard
      title="주간 타임라인"
      icon={CalendarRange}
      tail={`${start.getMonth() + 1}/${start.getDate()} ~ ${end.getMonth() + 1}/${end.getDate()}`}
      link={{ label: '캘린더', onClick: () => go('/calendar') }}
      loading={loading}
      error={error}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="fw-week">
        <div className="fw-week__head">
          <span aria-hidden />
          {days.map((d) => {
            const isToday = d.toDateString() === today.toDateString();
            return (
              <span key={d.toISOString()} className={isToday ? 'is-today' : undefined}>
                {['월', '화', '수', '목', '금', '토', '일'][(d.getDay() + 6) % 7]} {d.getDate()}
              </span>
            );
          })}
        </div>
        {/* ★ 종일 줄 — 있을 때만 세운다. 하나도 없는데 빈 띠가 남으면 시간 칸만 좁아진다. */}
        {allDay.segments.length > 0 && (
          <div className="fw-week__allday">
            <span className="fw-week__adk">종일</span>
            {allDay.segments.map((seg) => {
              const meta = TYPE_COLOR_MAP[seg.event.type as CalendarEventType];
              return (
                <button
                  type="button"
                  key={`${seg.event.id}-${seg.colStart}`}
                  className={`fw-adev${seg.continuesLeft ? ' is-contl' : ''}${seg.continuesRight ? ' is-contr' : ''}`}
                  style={{
                    gridColumn: `${seg.colStart + 2} / span ${seg.colEnd - seg.colStart + 1}`,
                    gridRow: seg.lane + 1,
                    ...(meta ? { background: meta.color } : {}),
                  }}
                  onClick={() => go('/calendar')}
                  title={seg.event.title}
                >
                  {seg.event.title}
                </button>
              );
            })}
            {allDay.more.map((n, i) => (n > 0 ? (
              <span key={`m${i}`} className="fw-admore" style={{ gridColumn: i + 2, gridRow: 3 }}>+{n}</span>
            ) : null))}
          </div>
        )}
        <div className="fw-week__body">
          <div className="fw-week__hours" aria-hidden>
            {[9, 11, 13, 15, 17].map((h) => <i key={h}>{String(h).padStart(2, '0')}</i>)}
          </div>
          {days.map((d) => {
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const isToday = d.toDateString() === today.toDateString();
            const rows = byDay.get(key) ?? [];
            return (
              <div key={key} className={`fw-week__col${isToday ? ' is-today' : ''}`}>
                {rows.slice(0, 6).map((e) => {
                  const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
                  const s = Math.max(DAY_FROM_MIN, minutesOfDay(e.startAt));
                  const en = Math.min(DAY_TO_MIN, Math.max(s + 30, minutesOfDay(e.endAt)));
                  const top = ((s - DAY_FROM_MIN) / DAY_SPAN_MIN) * 100;
                  const height = ((en - s) / DAY_SPAN_MIN) * 100;
                  return (
                    <button
                      type="button" key={e.id} className="fw-ev"
                      style={{ top: `${top}%`, height: `${height}%`, ...(meta ? { background: meta.color } : {}) }}
                      onClick={() => go('/calendar')} title={`${hhmm(e.startAt)} ${e.title}`}
                    >
                      {hhmm(e.startAt)} {e.title}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {/* 지금 — 시간대 안에 있을 때만. 밖이면 선이 가장자리에 붙어 거짓말을 한다. */}
          {nowPct >= 0 && nowPct <= 100 && <span className="fw-now" style={{ top: `${nowPct}%` }} aria-hidden />}
        </div>
      </div>
    </WidgetCard>
  );
}

/* ── ② 회의실 현황 ──────────────────────────────────────────────────────── */

/** 자원 하나의 «지금» — 진행 중인 예약과 다음 예약. 판정은 화면이 한다(서버는 사실만 준다). */
function roomNow(resource: ReservationResourceDto, rows: ReservationDto[], now: number) {
  const mine = rows
    .filter((r) => r.resourceId === resource.id)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  const current = mine.find((r) => new Date(r.startAt).getTime() <= now && new Date(r.endAt).getTime() > now) ?? null;
  const next = mine.find((r) => new Date(r.startAt).getTime() > now) ?? null;
  /* 칸 여섯 — 자원의 운영 시간을 여섯으로 나눠 «얼마나 찼나» 를 한눈에. */
  const open = resource.openMinute;
  const close = Math.max(open + 60, resource.closeMinute);
  const slots = Array.from({ length: 6 }, (_, i) => {
    const from = open + ((close - open) * i) / 6;
    const to = open + ((close - open) * (i + 1)) / 6;
    return mine.some((r) => {
      const s = new Date(r.startAt);
      const e = new Date(r.endAt);
      return s.getHours() * 60 + s.getMinutes() < to && e.getHours() * 60 + e.getMinutes() > from;
    });
  });
  return { current, next, slots, count: mine.length };
}

/**
 * 다음 예약 한 줄 — 시각에 **볼 수 있는 사유**를 붙인다 (시안의 「15:00 스프린트 리뷰」).
 *
 * ★★★ **사유는 언제나 오는 것이 아니다.** 서버가 «내 예약이 아니면 `purpose` 를 지워»
 *     보낸다(`reservation.routes.ts` — `/slots` 와 같은 규칙). 남이 무슨 일로 잡았는지는
 *     알 것이 아니라는 결정이다. 그러니 **시안처럼 모든 줄에 제목이 붙지는 않는다** —
 *     그때는 시각만 적는다. 없는 것을 「(비공개)」 같은 말로 채우지도 않는다.
 */
function nextText(next: ReservationDto): string {
  const at = hhmm(next.startAt);
  const why = next.purpose?.trim();
  return why ? `${at} ${why}` : `다음 예약 ${at}`;
}

/** 회의실 현황 위젯 (시안 화면 F ②) — 오늘, 지금. */
export function RoomStatusWidget() {
  const { go } = useWidgetActions();
  const chrome = useWidgetChrome();
  const gate = useMenuGate('reservation', '예약');
  const category = chrome.settings.resourceCategory;
  const { data, loading, error } = useRoomStatus(category, gate.enabled);

  const now = Date.now();
  const resources = data?.resources ?? [];
  const rows = data?.reservations ?? [];
  const cells = resources.slice(0, 8).map((r) => ({ resource: r, ...roomNow(r, rows, now) }));
  const busy = cells.filter((c) => c.current !== null).length;

  return (
    <WidgetCard
      title="회의실 현황"
      icon={Users}
      /* 「지금 N/M」은 아래 줄이 이미 말한다 — 제목 뒤에는 «무엇을 걸러 보고 있나» 를 둔다. */
      tail={category ?? null}
      link={{ label: '예약하기', onClick: () => go('/reservation/book') }}
      loading={loading}
      error={error}
      empty={!loading && !error && resources.length === 0
        ? (category ? `「${category}」 분류에 등록된 자원이 없습니다` : '등록된 예약 자원이 없습니다')
        : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="fw-rooms">
        <div className="fw-rooms__bar">
          <span>{category ?? '전체'} · 지금 <b>{busy}/{cells.length}</b> 사용 중</span>
          <span className="fw-rooms__legend">
            <i className="fw-dot fw-dot--free" aria-hidden />비어 있음
            <i className="fw-dot fw-dot--busy" aria-hidden />사용 중
          </span>
        </div>
        <div className="fw-rooms__grid">
          {cells.map(({ resource, current, next, slots }) => (
            <button
              type="button" key={resource.id}
              className={`fw-room ${current ? 'is-busy' : 'is-free'}`}
              onClick={() => go('/reservation/book')}
            >
              <span className="fw-room__n">
                <i className={`fw-dot ${current ? 'fw-dot--busy' : 'fw-dot--free'}`} aria-hidden />
                {resource.name}
                {/* ★ 정원 — 시안의 「회의실 A (8인)」. 「몇 명이 들어가나」는 방을 고를 때 먼저 보는 것이다.
                       0 이면 안 적는다(차량·장비처럼 정원이 뜻 없는 자원이 있다 — 「0인」은 거짓말이다). */}
                {resource.capacity > 0 && <em className="fw-room__cap">{resource.capacity}인</em>}
              </span>
              <span className="fw-room__d">
                {current
                  ? `사용 중 · ~${hhmm(current.endAt)}`
                  : next
                    ? nextText(next)
                    : '오늘 예약 없음'}
              </span>
              <span className="fw-room__slots" aria-hidden>
                {slots.map((on, i) => <i key={i} className={on ? 'on' : undefined} />)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
}

/* ── ③ 포토월 ───────────────────────────────────────────────────────────── */

/**
 * 포토월 위젯 (시안 화면 F ③).
 *
 * ★★★ **사진의 출처는 워크스페이스 폴더다**(2026-09-09 결정). 「어디서 가져올지」를 정하지 않고
 *     만들면 빈 그림을 지어내는 위젯이 된다(설계 §12). 워크스페이스를 고른 이유:
 *     ① 이미 올려 둔 사진이 거기 있고 ② 접근 권한을 **서버가 이미 지키며**
 *     ③ 새 표도, 새 관리 화면도 필요 없다.
 * ★ 고르기 전에는 **요청하지 않는다** — 「폴더를 고르세요」라고 적는다.
 */
export function PhotoWallWidget() {
  const { go } = useWidgetActions();
  const chrome = useWidgetChrome();
  const folderId = chrome.settings.photoFolderId;
  const gate = useMenuGate('workspace', '워크스페이스');
  const { data, loading, error } = usePhotoWall(gate.enabled ? folderId : undefined);
  const photos = data ?? [];

  return (
    <WidgetCard
      title="팀 갤러리"
      icon={ImageIcon}
      loading={loading}
      error={error}
      empty={!folderId
        ? '위젯 설정에서 사진이 든 폴더를 고르세요'
        : !loading && !error && photos.length === 0 ? '그 폴더에 사진이 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="fw-photos">
        {photos.map((p) => (
          <button type="button" key={p.id} className="fw-photo" onClick={() => go('/workspace')} title={p.name}>
            <img src={p.url} alt={p.name} loading="lazy" />
          </button>
        ))}
        {chrome.settings.photoFolderName && (
          <span className="fw-photos__cap">{chrome.settings.photoFolderName} · 사진 {photos.length}장</span>
        )}
      </div>
    </WidgetCard>
  );
}

/* ── ④ KPI 스트립 ───────────────────────────────────────────────────────── */

interface Kpi { key: string; label: string; value: string; unit?: string; to?: string }

/** 분을 「N.N」 시간으로. 못 읽으면 «모른다». */
function hours(min: number | null | undefined): string | null {
  if (min == null || !Number.isFinite(min)) return null;
  return (min / 60).toFixed(1);
}

/**
 * KPI 스트립 위젯 (시안 화면 F ④).
 *
 * ★★★ **모르는 값은 0 이 아니라 「—」다.** 옛 서버라 라우트가 없거나 이번 달 기록이 없으면
 *     0 으로 적지 않는다 — 「일을 안 했다」와 「모른다」는 다른 말이다(판 13 에서 겪은 것).
 * ⚠ 시안에는 칸마다 증감(+12%)과 스파크라인이 있다. **여기서는 그리지 않는다** — 지난 달을
 *   한 번 더 받아야 나오는 값이라, 없는 것을 그럴듯하게 지어내는 대신 «지금 값»만 적는다.
 *   지난 기간을 함께 받게 되면 그때 붙인다.
 */
export function KpiStripWidget() {
  return DASHBOARD_V2_ENABLED ? <KpiStripWidgetV2 /> : <KpiStripWidgetLegacy />;
}

function KpiStripWidgetLegacy() {
  const { go } = useWidgetActions();
  const attGate = useMenuGate('attendance', '근태');
  const apprGate = useMenuGate('approval', '전자결재');
  const taskGate = useMenuGate('task', '할일');
  const calGate = useMenuGate('calendar', '캘린더');

  const leave = useLeaveSummary();
  const daily = useMonthDailyWork(attGate.enabled);
  const done = useCompletedApprovals(apprGate.enabled);
  const projects = useProjectCount(taskGate.enabled);
  const now = new Date();
  const events = useMonthEvents(now.getFullYear(), now.getMonth(), calGate.enabled);

  const kpis = useMemo<Kpi[]>(() => {
    const out: Kpi[] = [];

    /* 이번 달 끝낸 결재 — 100건에서 잘렸으면 「100+」로 적는다(다 세지 않았다는 사실을 감추지 않는다). */
    if (apprGate.enabled) {
      const items = done.data?.items ?? [];
      const inMonth = items.filter((d) => {
        /* 「끝난 때」가 진실이다 — 없으면(옛 문서·옛 서버) 상신한 때, 그것도 없으면 만든 때. */
        const t = new Date(d.completedAt ?? d.draftedAt ?? d.createdAt);
        return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth();
      }).length;
      out.push({
        key: 'approval',
        label: '이번 달 완료 결재',
        value: done.data ? `${inMonth}${done.data.truncated ? '+' : ''}` : '—',
        unit: '건',
        // ★ 끝낸 결재를 세는 칸이라 **완료함**으로 간다 (2026-09-15) — 종전 `/approval/pending`(결재 대기)은 다른 함이었다.
        to: boxPath('done'),
      });
    }

    if (attGate.enabled) {
      const days = (daily.data?.days ?? []).filter((d) => d.creditedMinutes > 0);
      const avg = days.length > 0
        ? hours(days.reduce((s, d) => s + d.creditedMinutes, 0) / days.length)
        : null;
      out.push({ key: 'work', label: '하루 평균 근무', value: avg ?? '—', unit: '시간', to: '/erp/attendance' });
    }

    if (calGate.enabled) {
      const mins = (events.data ?? [])
        .filter((e) => !e.isAllDay)
        .reduce((s, e) => s + Math.max(0, (new Date(e.endAt).getTime() - new Date(e.startAt).getTime()) / 60000), 0);
      out.push({
        key: 'meeting',
        label: '이번 달 일정 시간',
        value: events.data ? hours(mins) ?? '—' : '—',
        unit: '시간',
        to: '/calendar',
      });
    }

    out.push({
      key: 'leave',
      label: '남은 연차',
      /* ★ 「잔여」는 `available`(잔여 − 신청중)이다 — 화면마다 숫자가 달라지지 않게 공용 함수로. */
      value: leaveAvailable(leave.data?.annual)?.toString() ?? '—',
      unit: '일',
      to: '/erp/attendance/me',
    });

    if (taskGate.enabled) {
      out.push({
        key: 'project',
        label: '진행 프로젝트',
        value: projects.data !== undefined ? String(projects.data) : '—',
        unit: '개',
        to: '/project',
      });
    }
    return out;
  }, [done.data, daily.data, events.data, leave.data, projects.data,
    apprGate.enabled, attGate.enabled, calGate.enabled, taskGate.enabled]);

  return (
    <WidgetCard
      title="한눈에"
      icon={BarChart3}
      loading={leave.loading && daily.loading}
      empty={kpis.length === 0 ? '보여 줄 지표가 없습니다 — 메뉴가 모두 꺼져 있습니다' : null}
      flush
    >
      <div className="fw-kpis">
        {kpis.map((k) => (
          <button
            type="button" key={k.key} className="fw-kpi"
            onClick={() => k.to && go(k.to)} disabled={!k.to}
          >
            <span className="fw-kpi__k">{k.label}</span>
            <span className="fw-kpi__v">
              {k.value}
              {k.unit && <span className="fw-kpi__u">{k.unit}</span>}
            </span>
          </button>
        ))}
      </div>
    </WidgetCard>
  );
}

/* ── ⑤ 공지 배너 ────────────────────────────────────────────────────────── */

/**
 * 공지 배너 위젯 (시안 화면 F ⑤).
 *
 * ★★ 기존 「공지사항」(목록)과 **다른 물건**이다 — 목록은 여럿을 훑는 것이고, 배너는 **하나를
 *    크게** 세운다. 그래서 필독이 있으면 그것을, 없으면 최신 하나를 세운다.
 * ★ 바탕은 회사가 고른 테마에서 파생한다(시안의 보라 그라디언트를 그대로 박지 않는다).
 */
/**
 * 공지를 쓴 사람의 이름 — 없으면 `null`.
 *
 * ★ 옛 기록이나 지워진 계정이면 이름이 빈 글자로 올 수 있다. 그때 「 · 」만 남으면
 *   **빈 자리가 있는 것처럼** 보인다 — 아예 그 도막을 뺀다.
 */
function authorName(n: NoticeListItem): string | null {
  const name = n.author?.name?.trim();
  return name ? name : null;
}

export function NoticeBannerWidget() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('board', '게시판');
  const { data, loading, error } = useNotices(gate.enabled);
  const rows = data ?? [];
  const pick = rows.find((n) => n.isImportant) ?? rows[0];

  return (
    <WidgetCard
      title="사내 공지"
      icon={Megaphone}
      loading={loading}
      error={error}
      empty={!loading && !error && !pick ? '공지사항이 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      {pick && (
        <button type="button" className="fw-banner" onClick={() => go('/board')}>
          <span className="fw-banner__bg" aria-hidden />
          <span className="fw-banner__in">
            <span className="fw-banner__tag">{pick.isImportant ? '필독' : '공지'}</span>
            <span className="fw-banner__t">{pick.title}</span>
            <span className="fw-banner__s">
              {/* ★ 쓴 사람 — 시안의 「회사 게시판 · 김태리 · 09-22 · …」 중 우리가 «받는» 칸이다.
                     이름이 비어 오면 그 도막을 통째로 뺀다(「 · · 9월 22일」 이 되지 않게). */}
              {authorName(pick) && `${authorName(pick)} · `}
              {/* ⚠ 로케일을 손으로 박지 않는다 — 화면 언어를 따라가야 한다(i18n 검사가 막는다). */}
              {new Date(pick.postedAt).toLocaleDateString(getDateLocale(), { month: 'long', day: 'numeric' })}
              {rows.length > 1 && ` · 공지 ${rows.length}건`}
            </span>
            <span className="fw-banner__btn">자세히 보기</span>
          </span>
        </button>
      )}
    </WidgetCard>
  );
}
